import React, { useState, useEffect } from 'react'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { createBoletoPayment } from '../services/paymentService'
import { Input } from '../../../shared/components'

const CSS_HANDLES = [
  'boletoPayment',
  'boletoInfo',
  'digitableLine',
  'copyButton',
  'downloadButton',
  'instructions'
] as const

interface BoletoPaymentProps {
  amount: number
  customerEmail: string
  customerName: string
  customerDocument: string
  orderId?: string | null
  onSuccess?: (paymentId: string) => void
  onError?: (error: string) => void
  className?: string
  // Dados de visualização (opcionais)
  existingPaymentData?: {
    boletoUrl?: string
    boletoBarcode?: string
    boletoDigitableLine?: string
    paymentId?: string
    expiresAt?: string
    status?: string
  }
}

const BoletoPayment: React.FC<BoletoPaymentProps> = ({
  amount,
  customerEmail,
  customerName,
  customerDocument,
  orderId,
  onSuccess,
  onError,
  className = '',
  existingPaymentData
}) => {
  const handles = useCssHandles(CSS_HANDLES)
  const [isLoading, setIsLoading] = useState(!existingPaymentData)
  const [boletoUrl, setBoletoUrl] = useState<string | null>(existingPaymentData?.boletoUrl || null)
  const [digitableLine, setDigitableLine] = useState<string | null>(existingPaymentData?.boletoDigitableLine || null)
  const [paymentId, setPaymentId] = useState<string | null>(existingPaymentData?.paymentId || null)
  const [expiresAt, setExpiresAt] = useState<string | null>(existingPaymentData?.expiresAt || null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const hasGeneratedBoleto = React.useRef(!!existingPaymentData)
  const isGenerating = React.useRef(false)

  useEffect(() => {
    // Só gera se tiver orderId e ainda não tiver gerado e não houver dados existentes
    if (orderId && !hasGeneratedBoleto.current && !isGenerating.current && !existingPaymentData) {
      generateBoleto()
    }
  }, [amount, customerEmail, customerName, customerDocument, orderId, existingPaymentData])

  const generateBoleto = async (isRegeneration = false) => {
    if (!orderId || isGenerating.current || (hasGeneratedBoleto.current && !isRegeneration)) return
    
    isGenerating.current = true
    setIsLoading(true)
    setError(null)

    try {
      const dueDate = new Date()
      dueDate.setDate(dueDate.getDate() + 3) // 3 dias para vencimento

      const response = await createBoletoPayment({
        orderId,
        amount,
        customerEmail,
        customerName,
        customerDocument,
        description: `Compra Baterpower Baterias - ${amount.toFixed(2)}`,
        dueDate: dueDate.toISOString()
      })

      setBoletoUrl(response.boletoUrl)
      setDigitableLine(response.boletoDigitableLine)
      setPaymentId(response.paymentId)
      setExpiresAt(response.expiresAt)
      hasGeneratedBoleto.current = true
      onSuccess?.(response.paymentId)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao gerar boleto'
      setError(message)
      onError?.(message)
      hasGeneratedBoleto.current = false // Permitir tentar novamente em caso de erro
    } finally {
      setIsLoading(false)
      isGenerating.current = false
    }
  }

  const handleCopyDigitableLine = async () => {
    if (!digitableLine) return

    try {
      await navigator.clipboard.writeText(digitableLine)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch (err) {
      console.error('Erro ao copiar:', err)
    }
  }

  const handleDownloadBoleto = () => {
    if (!boletoUrl) return
    window.open(boletoUrl, '_blank')
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const formatDigitableLine = (line: string) => {
    // Formata a linha digitável em grupos
    return line.replace(/(\d{5})(\d{5})(\d{5})(\d{6})(\d{5})(\d{6})(\d{1})(\d{14})/, 
      '$1.$2 $3.$4 $5.$6 $7 $8')
  }

  // Verificar se temos os dados essenciais do boleto
  const hasBoletoData = digitableLine && boletoUrl

  if (isLoading || (!hasBoletoData && !error)) {
    return (
      <div className={`${handles.boletoPayment} ${className} text-center py-8`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Gerando boleto bancário...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`${handles.boletoPayment} ${className}`}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
          <p className="font-medium mb-2">Erro ao gerar boleto</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={() => generateBoleto(true)}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`${handles.boletoPayment} ${className}`}>
      <div className={`${handles.instructions} bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6`}>
        <h3 className="font-semibold text-blue-900 mb-2">📄 Como pagar com Boleto</h3>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>Baixe ou copie a linha digitável do boleto</li>
          <li>Pague em qualquer banco, lotérica ou app</li>
          <li>O pagamento pode levar até 2 dias úteis para compensar</li>
          <li>Você receberá uma confirmação por e-mail</li>
        </ol>
      </div>

      <div className={`${handles.boletoInfo} bg-white border border-gray-200 rounded-lg p-6 mb-6`}>
        <div className="text-center mb-6">
          <p className="text-2xl font-bold text-gray-900 mb-2">
            {formatPrice(amount)}
          </p>
          {expiresAt && (
            <p className="text-sm text-gray-600">
              Vencimento: {new Date(expiresAt).toLocaleDateString('pt-BR')}
            </p>
          )}
        </div>

        <div className="space-y-4">
          {digitableLine && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Linha Digitável:
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={formatDigitableLine(digitableLine)}
                  readOnly
                  className={`${handles.digitableLine} flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono`}
                />
                <button
                  onClick={handleCopyDigitableLine}
                  disabled={!digitableLine}
                  className={`${handles.copyButton} px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {copied ? '✓ Copiado!' : 'Copiar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {boletoUrl && (
        <div className="flex gap-4 mb-6">
          <button
            onClick={handleDownloadBoleto}
            className={`${handles.downloadButton} flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center`}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Baixar Boleto PDF
          </button>
        </div>
      )}

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-medium text-yellow-900 mb-1">
              Atenção ao prazo de vencimento
            </p>
            <p className="text-sm text-yellow-800">
              O boleto vence em 3 dias. Após o vencimento, um novo boleto deverá ser gerado.
            </p>
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-500 text-center">
        <p>🔒 Pagamento seguro via boleto bancário</p>
        <p className="mt-1">ID do Pagamento: {paymentId}</p>
      </div>
    </div>
  )
}

export default BoletoPayment

